'use client'

import TeamMemberCard from '@/components/shared/TeamMemberCard'
import TeamSearchInput from './TeamSearchInput'
import debounce from 'lodash.debounce'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const WP_API = 'https://cms-dev.sasin.edu/api/v1/cpt'

const TeamSearchGrid = ({ posts = [], memberIds = [], children, filterSlot, filterChipSlot }) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Initialize from URL param
  const initialQuery = searchParams.get('s') || ''
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [searchResults, setSearchResults] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const hasInitialized = useRef(false)
  const prevMemberIdsRef = useRef(memberIds)

  // Create set of allowed member IDs for filtering search results
  const allowedIds = useMemo(() => new Set(memberIds), [memberIds])

  // Update URL with search query (without triggering navigation)
  const updateUrl = useCallback(
    (query) => {
      const params = new URLSearchParams(searchParams.toString())
      if (query && query.trim().length >= 2) {
        params.set('s', query.trim())
      } else {
        params.delete('s')
      }
      const newUrl = `${pathname}?${params.toString()}`
      window.history.replaceState(null, '', newUrl)
    },
    [pathname, searchParams]
  )

  // Search function (not debounced)
  const searchTeamMembers = useCallback(
    async (query, idsSet) => {
      if (!query || query.trim().length < 2) {
        setSearchResults(null)
        return
      }

      setIsSearching(true)
      try {
        const url = `${WP_API}?type=team_member&s=${encodeURIComponent(query)}&perPage=100`
        const response = await fetch(url)
        const data = await response.json()

        // Filter results to only include members from this category
        const filtered =
          idsSet.size > 0
            ? (data?.posts || []).filter((post) => idsSet.has(post?.id))
            : data?.posts || []

        setSearchResults(filtered)
      } catch (error) {
        console.error('Search error:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    },
    []
  )

  // Debounced version of search and URL update
  const debouncedSearch = useMemo(
    () =>
      debounce((query, idsSet) => {
        searchTeamMembers(query, idsSet)
        updateUrl(query)
      }, 500),
    [searchTeamMembers, updateUrl]
  )

  // Handle input change
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)

    if (value.trim().length >= 2) {
      debouncedSearch(value, allowedIds)
    } else {
      debouncedSearch.cancel()
      setSearchResults(null)
      updateUrl('')
    }
  }

  // Handle clear
  const handleClear = () => {
    debouncedSearch.cancel()
    setSearchQuery('')
    setSearchResults(null)
    updateUrl('')
  }

  // Initial search if URL has s param (only on mount)
  useEffect(() => {
    if (!hasInitialized.current && initialQuery && initialQuery.trim().length >= 2) {
      hasInitialized.current = true
      searchTeamMembers(initialQuery, allowedIds)
    }
  }, [initialQuery, searchTeamMembers, allowedIds])

  // Re-run search when memberIds change (category changed)
  useEffect(() => {
    // Check if memberIds actually changed
    const prevIds = prevMemberIdsRef.current
    const idsChanged = 
      prevIds.length !== memberIds.length ||
      prevIds.some((id, i) => id !== memberIds[i])

    if (idsChanged && searchQuery.trim().length >= 2) {
      // Re-run search with new member IDs
      searchTeamMembers(searchQuery, allowedIds)
    }

    prevMemberIdsRef.current = memberIds
  }, [memberIds, searchQuery, searchTeamMembers, allowedIds])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Display posts: search results when searching, otherwise original posts
  const displayPosts = searchResults !== null ? searchResults : posts

  return (
    <>
      {/* Search bar */}
      <div className="tw:flex tw:sm:flex-row tw:flex-col tw:items-center tw:gap-2 tw:max-w-4xl tw:mx-auto tw:mb-4">
        <div className="tw:flex-1 tw:max-sm:w-full">
          <TeamSearchInput
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onClear={handleClear}
            isSearching={isSearching}
          />
        </div>
        {filterSlot}
      </div>

      {/* Active filter chip */}
      {filterChipSlot}

      {/* Chip navigation slot */}
      {children}

      {/* Results count when searching */}
      {searchResults !== null && (
        <p className="tw:text-center tw:text-sm tw:text-neutral-500 tw:my-4">
          Found {displayPosts.length} result
          {displayPosts.length !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Team member grid */}
      <div className="tw:grid tw:grid-cols-2 tw:sm:grid-cols-4 tw:gap-4 tw:sm:gap-8 tw:my-12">
        {displayPosts.length > 0 ? (
          displayPosts.map((post) => (
            <TeamMemberCard key={post?.id} post={post} />
          ))
        ) : searchResults !== null ? (
          <div className="tw:col-span-full tw:text-center tw:py-12 tw:text-neutral-500">
            <i className="far fa-search tw:text-4xl tw:mb-4 tw:block tw:opacity-50" />
            <p>No team members found matching "{searchQuery}"</p>
          </div>
        ) : null}
      </div>
    </>
  )
}

export default TeamSearchGrid
